/**
 * Investor-Annahmen (#1138 Tag 3) — Notes-Liste, Admin kann editieren.
 * #1194 v2.576.0 — Personal-Modell-Editor (Sprungfix) als erste Card.
 */
import { useEffect, useState } from 'react';
import { useInvestorAuth } from '../../context/InvestorAuthContext';
import { Save, RotateCcw, Info } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function fmtEur(v) {
  if (v === null || v === undefined) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtNum(v) {
  if (v === null || v === undefined) return '–';
  return new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 }).format(v);
}
function labelize(p) {
  return p.replace(/^annahmen_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function PersonnelModelCard({ isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investor/scenario/personnel-model`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCfg(data);
        setDraft(JSON.parse(JSON.stringify(data)));
      }
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function updateSegment(segCode, field, value) {
    setDraft(d => ({
      ...d,
      segments: {
        ...d.segments,
        [segCode]: { ...d.segments[segCode], [field]: value },
      },
    }));
  }
  function updateCodesList(field, value) {
    const list = String(value).split(',').map(s => s.trim()).filter(Boolean);
    setDraft(d => ({ ...d, [field]: list }));
  }

  async function save() {
    setSaving(true);
    setErr(null);
    setOk(false);
    try {
      // Numeric coercion
      const payload = JSON.parse(JSON.stringify(draft));
      for (const segCode of Object.keys(payload.segments || {})) {
        const s = payload.segments[segCode];
        s.kosten_pro_stufe_eur = parseFloat(s.kosten_pro_stufe_eur) || 0;
        s.kapazitaet_pro_stufe = parseFloat(s.kapazitaet_pro_stufe) || 0;
        s.basis_kapazitaet = parseFloat(s.basis_kapazitaet) || 0;
      }
      const res = await fetch(`${API}/api/investor/scenario/personnel-model`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setCfg(updated);
      setDraft(JSON.parse(JSON.stringify(updated)));
      setOk(true);
      setTimeout(() => setOk(false), 3000);
    } catch (e) {
      setErr(e.message);
    } finally { setSaving(false); }
  }
  function reset() {
    if (cfg) setDraft(JSON.parse(JSON.stringify(cfg)));
    setErr(null);
    setOk(false);
  }

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 text-center text-xs text-[#9CA3AF]">
        Lade Personal-Modell…
      </div>
    );
  }
  if (!draft) return null;

  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 bg-[#F8F9FC] border-b border-[#E5E7EB] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-[#1A1A2E]">Personal-Modell (Sprungfix) — #1194</div>
          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-[#EEF2FF] text-[#4F46E5] border border-[#C7D2FE]">v{draft.version || 1}</span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            {dirty && (
              <button
                onClick={reset}
                className="text-xs px-2 py-1 rounded-md text-[#6B7280] hover:bg-[#F1F2F4] flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className="text-xs px-3 py-1 rounded-md bg-[#338edc] text-white disabled:bg-[#9CA3AF] disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Save className="w-3 h-3" /> {saving ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        )}
      </div>
      <div className="p-4 space-y-4">
        <div className="text-xs text-[#4B5563] bg-[#F8F9FC] border border-[#E5E7EB] rounded-md p-3 flex items-start gap-2">
          <Info className="w-4 h-4 text-[#338edc] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-[#1A1A2E] mb-1">Wie das Modell rechnet</div>
            <div>Steigt das AT-Volumen über die <strong>Basis-Kapazität × (1 + Effizienz-Hebel)</strong>, kommt automatisch eine neue <strong>20h/Woche-Stufe</strong> zu (= 0,5 VZÄ, default 1.600 €/Monat). Stufen sind ganzzahlig (immer aufgerundet) — fängt die echte Sprungfix-Kostenstruktur ein. DE-Volumen skaliert die Finecom-RevShare (5720) linear.</div>
          </div>
        </div>

        {/* Konto-Code-Pools */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#1A1A2E] mb-1">
              Personal-Konten (AT, Sprungfix-Pool)
            </label>
            <input
              type="text"
              value={(draft.personal_konto_codes || []).join(', ')}
              onChange={e => updateCodesList('personal_konto_codes', e.target.value)}
              disabled={!isAdmin}
              className="w-full text-xs px-2 py-1.5 border border-[#E5E7EB] rounded-md font-mono disabled:bg-[#F8F9FC]"
              placeholder="5710, 6000, 6070, 6140, 6148, 6230"
            />
            <div className="text-[10px] text-[#9CA3AF] mt-1">Komma-getrennt. Konten, die als Personal-Aufwand AT klassifiziert werden.</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1A1A2E] mb-1">
              Finecom-Konten (DE, linear)
            </label>
            <input
              type="text"
              value={(draft.finecom_konto_codes || []).join(', ')}
              onChange={e => updateCodesList('finecom_konto_codes', e.target.value)}
              disabled={!isAdmin}
              className="w-full text-xs px-2 py-1.5 border border-[#E5E7EB] rounded-md font-mono disabled:bg-[#F8F9FC]"
              placeholder="5720"
            />
            <div className="text-[10px] text-[#9CA3AF] mt-1">Komma-getrennt. RevShare-Konten, linear mit DE-Volumen.</div>
          </div>
        </div>

        {/* Segmente */}
        <div className="space-y-3">
          {Object.entries(draft.segments || {}).map(([segCode, seg]) => (
            <div key={segCode} className="border border-[#E5E7EB] rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-[#F8F9FC] border-b border-[#E5E7EB] flex items-center justify-between">
                <div className="text-sm font-medium text-[#1A1A2E] capitalize">{segCode}</div>
                <span className="text-[10px] text-[#6B7280]">{seg.einheit || '—'}</span>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <FieldNum
                  label="Kosten pro Stufe (20h/W)"
                  value={seg.kosten_pro_stufe_eur}
                  onChange={v => updateSegment(segCode, 'kosten_pro_stufe_eur', v)}
                  disabled={!isAdmin}
                  suffix="€/Monat"
                  hint="Lohnkosten einer 0,5-VZÄ-Stufe pro Monat."
                />
                <FieldNum
                  label="Kapazität pro Stufe"
                  value={seg.kapazitaet_pro_stufe}
                  onChange={v => updateSegment(segCode, 'kapazitaet_pro_stufe', v)}
                  disabled={!isAdmin}
                  suffix={seg.einheit}
                  hint="Zusätzliche Bearbeitungs-Kapazität, die eine Stufe (0,5 VZÄ) im Monat leistet."
                />
                <FieldNum
                  label="Basis-Kapazität (heute)"
                  value={seg.basis_kapazitaet}
                  onChange={v => updateSegment(segCode, 'basis_kapazitaet', v)}
                  disabled={!isAdmin}
                  suffix={seg.einheit}
                  hint="Heutiges AT-Volumen, das mit der bestehenden Belegschaft ohne neue Stufen bewältigt wird."
                />
              </div>
            </div>
          ))}
        </div>

        {err && <div className="text-xs text-[#DC2626] bg-[#FEE2E2] border border-[#FCA5A5] rounded-md px-3 py-2">{err}</div>}
        {ok && <div className="text-xs text-[#15803D] bg-[#DCFCE7] border border-[#86EFAC] rounded-md px-3 py-2">Gespeichert.</div>}
      </div>
    </div>
  );
}

function FieldNum({ label, value, onChange, disabled, suffix, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#1A1A2E] mb-1">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          step="any"
          className="w-full text-xs px-2 py-1.5 border border-[#E5E7EB] rounded-md tabular-nums disabled:bg-[#F8F9FC]"
        />
        {suffix && <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#9CA3AF] pointer-events-none">{suffix}</div>}
      </div>
      {hint && <div className="text-[10px] text-[#9CA3AF] mt-1">{hint}</div>}
    </div>
  );
}

export default function InvestorAnnahmen() {
  const { user } = useInvestorAuth();
  const isAdmin = user?.role === 'admin';
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editWert, setEditWert] = useState('');

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      const res = await fetch(`${API}/api/investor/annahmen?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRows(data.rows || []);
      }
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [year]); // eslint-disable-line

  async function save(id) {
    const res = await fetch(`${API}/api/investor/annahmen/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ notes: editNotes, wert_eur: editWert === '' ? null : parseFloat(editWert) }),
    });
    if (res.ok) {
      setEditId(null);
      await load();
    } else {
      const d = await res.json();
      alert('Fehler: ' + (d.error || res.status));
    }
  }

  function startEdit(r) {
    setEditId(`${r.position}-${r.monat}`);
    setEditNotes(r.notes || '');
    setEditWert(r.wert_eur ?? '');
  }

  const grouped = rows.reduce((acc, r) => {
    if (!acc[r.position]) acc[r.position] = [];
    acc[r.position].push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[#1A1A2E]">Annahmen & Notizen</h1>
          <div className="text-xs text-[#6B7280] mt-0.5">Plan-Hintergrund, Modellannahmen, Kommentare</div>
        </div>
        <select
          value={year}
          onChange={e => setYear(parseInt(e.target.value, 10))}
          className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
        >
          {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <PersonnelModelCard isAdmin={isAdmin} />

      {loading ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-xs text-[#9CA3AF]">Lädt…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-xs text-[#9CA3AF]">Keine Annahmen für {year}</div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([position, list]) => (
            <div key={position} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-[#F8F9FC] border-b border-[#E5E7EB] text-sm font-semibold text-[#1A1A2E]">
                {labelize(position)}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {list.map(r => {
                    const key = `${r.position}-${r.monat}`;
                    const isEditing = editId === key;
                    return (
                      <tr key={key} className="border-t border-[#F1F2F4]">
                        <td className="px-4 py-2 w-20 text-xs text-[#6B7280] tabular-nums">M{r.monat}</td>
                        <td className="px-4 py-2 w-32 text-right tabular-nums">
                          {isEditing && isAdmin ? (
                            <input
                              type="number"
                              value={editWert}
                              onChange={e => setEditWert(e.target.value)}
                              className="w-28 text-xs px-2 py-1 border border-[#E5E7EB] rounded-md text-right"
                            />
                          ) : fmtEur(r.wert_eur)}
                        </td>
                        <td className="px-4 py-2 text-xs text-[#4B5563]">
                          {isEditing && isAdmin ? (
                            <textarea
                              value={editNotes}
                              onChange={e => setEditNotes(e.target.value)}
                              rows={2}
                              className="w-full text-xs px-2 py-1 border border-[#E5E7EB] rounded-md"
                            />
                          ) : (r.notes || <span className="text-[#9CA3AF] italic">—</span>)}
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-2 w-28 text-right">
                            {isEditing ? (
                              <div className="flex items-center gap-1 justify-end">
                                <button onClick={() => save(r.id || key)} className="text-xs px-2 py-1 rounded-md bg-[#338edc] text-white">OK</button>
                                <button onClick={() => setEditId(null)} className="text-xs px-2 py-1 rounded-md text-[#6B7280]">×</button>
                              </div>
                            ) : (
                              <button onClick={() => startEdit(r)} className="text-xs text-[#338edc] hover:underline">Bearbeiten</button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
